import React from 'react';
import PropTypes from 'prop-types';

import useAppContext from '../../context/useAppContext';

import Chart from '../../models/chart';
import Categories from '../../pages/charts/chart/category/Categories';
import AddCategory from '../../pages/charts/chart/category/AddCategory';
import EditCategory from '../../pages/charts/chart/category/EditCategory';

import Inventories from '../../pages/charts/chart/inventory/Inventories';
import Inventory from '../../pages/charts/chart/inventory/Inventory';

import Zone from '../../pages/charts/chart/zone/Zone';
import Zones from '../../pages/charts/chart/zone/Zones';

import ChartCustomContainer from '../../pages/charts/chart/chart_custom/index';

import ContingentList from '../../pages/charts/contingent/index';
import ContingentCreateContainer from '../../pages/charts/contingent/create/index';
import ContingentEditContainer from '../../pages/charts/contingent/edit/index';
import { Route, Switch } from 'react-router-dom';
import Template from '../../components/template/template';
import Loading from '../../components/loading';
import ChartSettings from '../../pages/charts/chart/chart_settings/chart_settings';
import ChartHome from '../../pages/charts/home';
import PageError from '../../pages/error/pageError';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

export default function ChartRouter({ match: { path, url }, organization, chartUuid, history }) {
    const [context, setContext] = useAppContext();
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    let uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

    const [, loading] = Chart.useApiModel(
        {
            id: chartUuid,
            organizationId: organization.id,
            onSuccess: chart => {
                setContext({ chart });
            },
            onError: e => {
                if (e.code === 404) {
                    toast.error(t('pageNotFound'));
                } else {
                    toast.error(t('ticket.common.toast_error'));
                }

                history.push(`/ticket/O${organization.id}/charts`);
            }
        },
        [chartUuid, organization]
    );

    if (loading || !context.chart) {
        return <Loading />;
    }

    return (
        <Template organization={organization} chart={context.chart}>
            <Switch>
                <Route
                    exact
                    path={`${path}/dashboard`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <ChartHome organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/customs`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <ChartCustomContainer organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/global_settings`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <ChartSettings organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/categories`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <Categories organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/categories/:categoryId(${uuidRegex})`}
                    render={({
                        match: {
                            params: { chartUuid, categoryId }
                        }
                    }) => <EditCategory organization={organization} chartId={chartUuid} categoryId={categoryId} />}
                />
                <Route
                    exact
                    path={`${path}/categories/new`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <AddCategory organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/inventories`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <Inventories organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/inventories/:inventoryId(${uuidRegex})`}
                    render={({
                        match: {
                            params: { chartUuid, inventoryId }
                        }
                    }) => <Inventory organization={organization} chartId={chartUuid} inventoryId={inventoryId} />}
                />
                <Route
                    exact
                    path={`${path}/inventories/new`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <Inventory organization={organization} chartId={chartUuid} />}
                />

                <Route
                    exact
                    path={`${path}/zones`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <Zones organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/zones/new`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <Zone organization={organization} chartId={chartUuid} />}
                />
                <Route
                    exact
                    path={`${path}/zones/:zoneId(${uuidRegex})`}
                    render={({
                        match: {
                            params: { chartUuid, zoneId }
                        }
                    }) => <Zone organization={organization} chartId={chartUuid} zoneId={zoneId} />}
                />
                <Route
                    exact
                    path={`${path}/contingents/:contingentId(${uuidRegex})`}
                    render={({
                        match: {
                            params: { chartUuid, contingentId }
                        }
                    }) => <ContingentEditContainer organization={organization} chartId={chartUuid} contingentId={contingentId} />}
                />
                <Route
                    exact
                    path={`${path}/contingents/new`}
                    render={({
                        match: {
                            params: { chartUuid, contingentId }
                        }
                    }) => <ContingentCreateContainer organization={organization} chartId={chartUuid} contingentId={contingentId} />}
                />
                <Route
                    exact
                    path={`${path}/contingents`}
                    render={({
                        match: {
                            params: { chartUuid }
                        }
                    }) => <ContingentList organization={organization} chartId={chartUuid} />}
                />
                {/* Error */}
                <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/charts/${chartUuid}/dashboard`} />} />
            </Switch>
        </Template>
    );
}

ChartRouter.propTypes = {
    match: PropTypes.object,
    organization: PropTypes.object,
    chartUuid: PropTypes.string
};
